/**
 * resolve-receive-link.ts — Orchestration layer between UI and networking.
 *
 * ── PURPOSE ───────────────────────────────────────────────────────────────────
 * Single async function that takes any user input string and returns a
 * canonical { magnetUri } ready for TransferCoordinator.
 *
 * The ReceiveScreen calls ONLY this function — never individual protocol
 * parsers. The coordinator receives ONLY the resolved output.
 *
 * ── FLOW ──────────────────────────────────────────────────────────────────────
 *
 *  Input string
 *      │
 *      ▼
 *  classifyLink()  [synchronous, no network]
 *      │
 *      ├── 'invalid'           → throw TransferError('INVALID_LINK')
 *      │
 *      ├── 'magnet'            → return { magnetUri: input }
 *      │
 *      ├── 'raw-infohash'      → return { magnetUri: 'magnet:?xt=urn:btih:' + hash }
 *      │
 *      ├── 'bitdrop-payload'   → parsePayload → build minimal magnet
 *      │
 *      └── 'bitdrop-short-url' → resolveShortUrl() [HTTP]
 *                                     │
 *                                     ├── ok → { magnetUri }
 *                                     └── ShortUrlError → TransferError (mapped)
 *
 * ── INVARIANT ─────────────────────────────────────────────────────────────────
 * Every resolved magnetUri has already been validated by parseMagnet()
 * before this function returns it. The coordinator never receives unvalidated input.
 */

import {
  classifyLink,
  parsePayload,
  resolveShortUrl,
  ShortUrlError,
} from '@share/networking';
import { TransferError, type TransferErrorCode } from '../errors/transfer-errors';
import { buildMagnetUri } from '../../networking/protocol/magnet-uri';

// ── Output type ───────────────────────────────────────────────────────────────

export interface ResolvedReceiveLink {
  /** Canonical magnet URI — validated before return. Ready for TorrentEngine. */
  magnetUri: string;
}

// ── Resolver ──────────────────────────────────────────────────────────────────

/**
 * Classify and resolve any user-facing link string to a canonical magnetUri.
 *
 * @throws {TransferError} for all failure cases with user-facing messages.
 */
export async function resolveReceiveLink(input: string): Promise<ResolvedReceiveLink> {
  const classification = classifyLink(input);

  switch (classification.kind) {
    case 'invalid':
      throw new TransferError('INVALID_LINK', classification.reason!);

    case 'magnet':
      // Already validated by classifyLink → parseMagnet()
      return { magnetUri: classification.payload! };

    case 'raw-infohash':
      // Build minimal magnet from bare hex infoHash
      return { magnetUri: buildMagnetUri({ infoHash: classification.payload! }) };

    case 'bitdrop-payload': {
      // Parse BitDrop binary protocol packet → extract infoHash → minimal magnet
      const sharePayload = parsePayload(classification.payload!);
      if (!sharePayload.infoHash) {
        throw new TransferError('INVALID_LINK', 'This BitDrop link contains no download information.');
      }
      return { magnetUri: buildMagnetUri({ infoHash: sharePayload.infoHash }) };
    }

    case 'bitdrop-short-url': {
      // HTTP resolution — map ShortUrlError codes to TransferError codes
      try {
        const resolution = await resolveShortUrl(classification.payload!);
        return { magnetUri: resolution.magnetUri };
      } catch (err) {
        if (err instanceof ShortUrlError) {
          const codeMap: Record<string, TransferErrorCode> = {
            NOT_FOUND:       'SHORT_URL_NOT_FOUND',
            EXPIRED:         'SHORT_URL_EXPIRED',
            SERVICE_DOWN:    'SHORT_URL_SERVICE_DOWN',
            NETWORK_ERROR:   'SHORT_URL_NETWORK_ERROR',
            INVALID_TARGET:  'SHORT_URL_INVALID_TARGET',
          };
          const transferCode = codeMap[err.code] ?? 'SHORT_URL_SERVICE_DOWN';
          throw new TransferError(transferCode as TransferErrorCode, err.message);
        }
        throw new TransferError('SHORT_URL_SERVICE_DOWN', 'Failed to resolve BitDrop link. Try again.');
      }
    }
  }
}
